import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PortalUser } from 'src/app/feature-module/portal-users/models/PortalUser';
import { PortalUserInfo } from 'src/app/feature-module/portal-users/models/PortalUserInfo';
import { PortalUserProfile } from 'src/app/feature-module/portal-users/models/PortalUserProfile';
import { Role } from 'src/app/feature-module/portal-users/models/Role';
import { SearchPortalUsersCriteria } from 'src/app/feature-module/portal-users/models/SearchPortalUsersCriteria';
import { ConfigService } from 'src/app/core/services/config.service';
import { Constants } from 'src/app/shared/model/Constants';
import { ReferenceType } from 'src/app/shared/model/ReferenceType';
import { Ability } from '../models/Ability';
import { ResetPasswordLinkResponse } from '../models/ResetPasswordLinkResponse';
import { PortalUserRole } from '../models/PortalUserRole';
import { PortalUserAbility } from '../models/PortalUserAbility';
import { Restriction } from '../models/Restriction';
import { PortalUserRestriction } from '../models/PortalUserRestriction';


@Injectable({
  providedIn: 'root'
})
export class PortalUserService {

  constructor(private http: HttpClient,
    private configService: ConfigService) {
}

public searchUsers(criteria: SearchPortalUsersCriteria): Observable<PortalUserInfo[]> {
  return this.http.post<PortalUserInfo[]>(
    this.configService.UserWebApiUrl + 'SearchPortalUsers', criteria);
}

public getUserProfile(id: string): Observable<PortalUserProfile> {

  return this.http.get<PortalUserProfile>(this.configService.UserWebApiUrl + 'GetPortalUserProfile/' + id);
}

public updateUserProfile(userProfile: PortalUser): Observable<string> {

  return this.http.post<string>(this.configService.UserWebApiUrl + 'UpdatePortalUser', userProfile);
}

public registerUserProfile(userProfile: PortalUser): Observable<string> {

  return this.http.post<string>(this.configService.UserWebApiUrl + 'RegisterPortalUser', userProfile);
}

public removeUserRole(userId:string, roleId:number): Observable<string> {

  const postBody = { userId:userId , roleToAddIds : [], roleToRemoveIds:[roleId]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserRoles', postBody);
}

public addUserRole(userId:string, roleId:number): Observable<string> {

  const postBody = { userId:userId , roleToAddIds : [roleId], roleToRemoveIds:[]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserRoles', postBody);
}

public getAllRoles(): Observable<Role[]>{
  return this.http.get<Role[]>(this.configService.UserWebApiUrl + 'RoleDefinition');
}

public addUserAbility(portalUserId:string, abilityId:number): Observable<string> {

  const postBody = { portalUserId:portalUserId , abilitiesToAddIds : [abilityId], abilitiesToRemoveIds:[]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserAbilities', postBody);
}

public removeUserAbility(portalUserId:string, abilityId:number): Observable<string> {

  const postBody = { portalUserId:portalUserId , abilitiesToAddIds : [], abilitiesToRemoveIds:[abilityId]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserAbilities', postBody);
}

public getAllAbilities(): Observable<Ability[]>{
  return this.http.get<Ability[]>(this.configService.UserWebApiUrl + 'AbilityDefinition');
}

public addUserRestriction(portalUserId:string, restrictionId:number): Observable<string> {

  const postBody = { portalUserId:portalUserId , restrictionsToAddIds : [restrictionId], restrictionsToRemoveIds:[]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserRestrictions', postBody);
}

public removeUserRestriction(portalUserId:string, restrictionId:number): Observable<string> {

  const postBody = { portalUserId:portalUserId , restrictionsToAddIds : [], restrictionsToRemoveIds:[restrictionId]};
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ManageUserRestrictions', postBody);
}

public getAllRestrictions(): Observable<Restriction[]>{
  return this.http.get<Restriction[]>(this.configService.UserWebApiUrl + 'RestrictionDefinition');
}

public setPortalUserEnable(userId: string,isEnabled: boolean): Observable<string>{
  
  const postBody = {
    portalUserId : userId,
    isEnabled : isEnabled
  }

  return this.http.post<string>(this.configService.UserWebApiUrl + 'EnableOrDisablePortalUser',postBody );
}

public unlockPortalUser(userId: string): Observable<string>{
  
  const postBody = {
    id : userId,
  }

  return this.http.post<string>(this.configService.UserWebApiUrl + 'UnlockPortalUser',postBody );
}

public sendResetPasswordLink(userId: string): Observable<string>{
  
  const postBody = {
    id : userId,
  }
  return this.http.post<string>(this.configService.UserWebApiUrl + 'ResetPasswordLink',postBody );
}

public getResetPasswordLink(userId: string): Observable<ResetPasswordLinkResponse>{

  return this.http.get<ResetPasswordLinkResponse>(this.configService.UserWebApiUrl + 'ResetPasswordLink/' + userId );
}

public getLinkedUsers(referenceType:number, referenceId:number): Observable<PortalUserInfo[]>{
  return this.http.get<PortalUserInfo[]>(this.configService.UserWebApiUrl + 'PortalUserLinks/linkedUsers/' + referenceType + '/' + referenceId);
}

public getAllUsers(): Observable<PortalUserInfo[]>{
  return this.searchUsers({
      portalUserId: null,
      pageNumber: 1,
      rowsPerPage: Constants.MaxInt,
      sortDirection: '',
      sortColumn: ''
  });
}

public addUserLink(userId: string, referenceId:number, referenceType: ReferenceType, additionalData:string): Observable<string>{
  
  const postBody = {
    portalUserId : userId,
    referenceId: referenceId,
    referenceType: referenceType,
    additionalData: additionalData
  }

  return this.http.post<string>(this.configService.UserWebApiUrl + 'PortalUserLinks/add',postBody );
}

public removeUserLink(userId: string, referenceId:number, referenceType: ReferenceType): Observable<string>{
  
  const postBody = {
    portalUserId : userId,
    referenceId: referenceId,
    referenceType: referenceType,
  }

  return this.http.post<string>(this.configService.UserWebApiUrl + 'PortalUserLinks/remove',postBody );
}

public getAllPortalUsersRoles(): Observable<PortalUserRole[]> {
  return this.http.get<PortalUserRole[]>(this.configService.UserWebApiUrl + 'ManageUserRoles/getAllPortalUsersRoles');
}

public getAllPortalUsersAbilities(): Observable<PortalUserAbility[]> {
  return this.http.get<PortalUserAbility[]>(this.configService.UserWebApiUrl + 'ManageUserAbilities/getAllPortalUsersAbilities');
}

public getAllPortalUsersRestrictions(): Observable<PortalUserRestriction[]> {
  return this.http.get<PortalUserRestriction[]>(this.configService.UserWebApiUrl + 'ManageUserRestrictions/getAllPortalUsersRestrictions');
}

}
